import { Alert, Button, Col, Row } from 'antd'
import { clearHiddenMessages, getHiddenMessages } from '../../util/cookieUtil'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import AppContext from './AppContext'
import Message from './Message'
import _ from 'lodash'
import axios from '../../axios/axios'
import { getServerDate } from '../../util/serverTimeUtil'
import { isAfter } from 'date-fns'
import useResponsive from '../../hooks/useResponsive'

export const isHidden = (message, hiddenList) => {
  try {
    const hidden = Array.isArray(hiddenList) ? hiddenList : getHiddenMessages()
    const id = message?.id || message?.attributes?.id
    if (!id) return false

    // hidden can be an array of ids (legacy) or objects { id, updatedAt }
    for (const h of hidden) {
      if (!h) continue
      if (typeof h === 'string' || typeof h === 'number') {
        if (h === id) return true
        continue
      }
      if (typeof h === 'object' && h.id === id) {
        // If stored updatedAt is missing, treat as hidden.
        const storedUpdated = h.updatedAt ? new Date(h.updatedAt) : null
        const msgUpdated = message?.updatedAt || message?.attributes?.updatedAt
        if (!storedUpdated) return true
        if (!msgUpdated) return true
        try {
          if (new Date(msgUpdated) <= storedUpdated) return true
        } catch (e) {
          // fall through and treat as not hidden
        }
      }
    }
  } catch (e) {
    // ignore cookie errors and fall back to standard behavior
  }
  return false
}

// Returns true if the message is enabled and not expired
export const isActive = (message) => {
  const { visible, expires, heading, content } = message?.attributes ?? message ?? {}
  if (!visible || (!heading && !content)) {
    return false
  }
  if (expires && isAfter(getServerDate(), new Date(expires))) {
    return false
  }
  return true
}

export const shouldShow = (message, showAll, showHidden) => {
  if (showAll) {
    return true
  }
  if (!showHidden && isHidden(message)) {
    return false
  }
  return isActive(message)
}

const REFRESH_INTERVAL_MS = 1000 * 60 // 1 minute
const RELOAD_EVENT_NAME = 'htc-messages-reload'

const Messages = ({ adminMode, forceUpdate, showAll, visibleFirst, extraContent, extraContentProps = {} }) => {
  const [messages, setMessages] = useState()
  const [showHidden, setShowHidden] = useState(false)
  const [hasHiddenMessages, setHasHiddenMessages] = useState(false)
  const [tick, setTick] = useState(0)
  const { messageHidden, setMessageHidden } = useContext(AppContext)
  const bp = useResponsive()

  // const [isVisible, setIsVisible] = useState(true)
  const lastScrollY = useRef(0)

  // useEffect(() => {
  //   const handleScroll = () => {
  //     const currentScrollY = window.scrollY

  //     if (currentScrollY > lastScrollY.current && currentScrollY > 10) {
  //       setIsVisible(false) // Scroll Down
  //     } else {
  //       setIsVisible(true) // Scroll Up
  //     }
  //     lastScrollY.current = currentScrollY
  //   }

  //   window.addEventListener('scroll', handleScroll, { passive: true })

  //   return () => window.removeEventListener('scroll', handleScroll)
  // }, [])

  const fetchMessages = useCallback(async () => {
    try {
      console.log('Fetching messages at ', new Date().toISOString())
      const messages = await axios.get('/admin/message')
      let sorted = visibleFirst
        ? _.reverse(_.sortBy(messages.data.data, 'expires'))
        : _.reverse(_.sortBy(messages.data.data, 'updatedAt'))
      const hiddenIds = getHiddenMessages()
      const finalMessages = showAll ? sorted : sorted.filter((m) => shouldShow(m, showAll, showHidden))
      setMessages(finalMessages)
      // There are hidden messages if any active message is hidden
      setHasHiddenMessages(
        _.some(messages.data.data, (m) => {
          const active = isActive(m)
          const hidden = isHidden(m, hiddenIds)
          return active && hidden
        }),
      )
    } catch (e) {
      // Keep the existing behavior of not crashing on fetch failures.
      // `notifyError` is intentionally omitted here because it applies globally.
      console.error('Failed to reload messages', e)
    }
  }, [visibleFirst, showAll])

  useEffect(() => {
    fetchMessages()
    const interval = setInterval(fetchMessages, REFRESH_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchMessages, forceUpdate])

  useEffect(() => {
    const handleReload = (event) => {
      fetchMessages()
      // If the event explicitly requests to show all, update internal state
      if (event.detail?.showAll) {
        setShowHidden(true)
      }
    }
    window.addEventListener(RELOAD_EVENT_NAME, handleReload)
    return () => window.removeEventListener(RELOAD_EVENT_NAME, handleReload)
  }, [fetchMessages])

  const hiddenMessagesText = bp.xs ? '' : "You've hidden some messages. "
  const hiddenMessagesLinkText = bp.xs ? 'Show messages' : 'Show them again?'
  const hiddenMessageNotificationClassname = 'hiddenMessage'

  const messagesDiv = (
    <div className={adminMode ? 'adminMessagesWrapper' : 'messagesWrapper'} style={{ backgroundColor: '#ffffff8e' }}>
      {messages?.map((message, i) => {
        return shouldShow(message, showAll, showHidden) ? (
          <>
            {extraContent?.(message)}
            <Row gutter={20}>
              <Col flex={1}>
                <Message
                  adminMode={showAll}
                  key={i}
                  message={{
                    ...message,
                  }}
                />
              </Col>
            </Row>
          </>
        ) : null
      })}
    </div>
  )

  const hiddenMessagesNotificationDiv = (
    <Row style={{ textAlign: 'right', paddingTop: 0 }} gutter={0}>
      <Col flex={1}>
        {(messageHidden || (hasHiddenMessages && !showHidden)) && (
          <Alert
            className={hiddenMessageNotificationClassname}
            style={{ backgroundColor: 'transparent', border: 'none', paddingTop: 0 }}
            description={
              <div className="hiddenMessageNotification">
                {hiddenMessagesText}
                <Button
                  type="link"
                  className="noPad"
                  style={{ zIndex: 1001 }}
                  onClick={() => {
                    clearHiddenMessages()
                    window.location.reload() // Force full reload to ensure all messages are shown and cookies are cleared across the app
                    // setShowHidden(true)
                    // setMessageHidden(false)
                    // fetchMessages()
                    // setTimeout(() => {
                    //   setTick((tick) => tick + 1)
                    // }, 1)
                  }}
                >
                  {hiddenMessagesLinkText}
                </Button>
              </div>
            }
          />
        )}
      </Col>
    </Row>
  )

  const content = bp.xs ? (
    <div style={{ paddingTop: '2.5em' }}>
      {!showAll && hiddenMessagesNotificationDiv}
      {messagesDiv}
    </div>
  ) : (
    <>
      {messagesDiv}
      {!showAll && hiddenMessagesNotificationDiv}
    </>
  )

  return content
}

export default Messages
